import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { trackEvent } from '../../utils/analytics';
import './StyleBeta.css';

const EXPERIMENT = 'style_wtp_v1';
const SOURCE = 'instagram_beta';
const TRACKED = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
const ALLOWED = [...TRACKED, 'utm_term', 'campaign', 'adset', 'ad'];

function readParams(search) {
  const input = new URLSearchParams(search);
  return ALLOWED.reduce((result, key) => {
    const value = input.get(key);
    if (value !== null) result[key] = value;
    return result;
  }, {});
}

function RevealSection({ as: Tag = 'section', className = '', children, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!('IntersectionObserver' in window)) {
      node.classList.add('is-visible');
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      node.classList.add('is-visible');
      observer.disconnect();
    }, { threshold: 0.12 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <Tag ref={ref} className={'style-beta-reveal ' + className} {...props}>{children}</Tag>;
}

RevealSection.propTypes = {
  as: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

function AnimatedPreview({ t, areas }) {
  const [score, setScore] = useState(0);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame;
    let startTimer;
    let cycleTimer;
    let cancelled = false;
    const run = () => {
      setScore(0);
      startTimer = window.setTimeout(() => {
        const startedAt = performance.now();
        const tick = (now) => {
          if (cancelled) return;
          const progress = Math.min((now - startedAt) / 800, 1);
          setScore(Math.round(78 * progress));
          if (progress < 1) frame = window.requestAnimationFrame(tick);
        };
        frame = window.requestAnimationFrame(tick);
      }, 2300);
      cycleTimer = window.setTimeout(run, 6000);
    };
    const syncMotion = () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(cycleTimer);
      window.cancelAnimationFrame(frame);
      if (media.matches) setScore(78);
      else run();
    };
    syncMotion();
    media.addEventListener?.('change', syncMotion);
    return () => {
      cancelled = true;
      media.removeEventListener?.('change', syncMotion);
      window.clearTimeout(startTimer);
      window.clearTimeout(cycleTimer);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <article className="style-beta-preview" aria-labelledby="preview-title">
      <div className="style-beta-photo-placeholder" aria-hidden="true">
        <div className="style-beta-person-shape"><span /><i /></div>
        <div className="style-beta-scan-line" />
        <span className="style-beta-analysis-label">{t('style_beta.preview.analyzing')}</span>
      </div>
      <header>
        <div><p>{t('style_beta.preview.label')}</p><h2 id="preview-title">{t('style_beta.preview.score_title')}</h2></div>
        <strong aria-label={t('style_beta.preview.score_a11y', { score })}>{score}<span> / 100</span></strong>
      </header>
      <div className="style-beta-priority"><b>{t('style_beta.preview.priority_label')}</b><p>{t('style_beta.preview.priority_text')}</p></div>
      <ul>{areas.map((area, index) => (
        <li key={area.label} style={{ '--row-delay': (index * 0.13) + 's' }}><span>{area.label}</span><em className={'is-' + area.tone}>{area.status}</em></li>
      ))}</ul>
      <p className="style-beta-disclaimer">{t('style_beta.preview.disclaimer')}</p>
    </article>
  );
}

AnimatedPreview.propTypes = {
  t: PropTypes.func.isRequired,
  areas: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    tone: PropTypes.string.isRequired,
  })).isRequired,
};

export default function StyleBeta() {
  const { t, i18n } = useTranslation();
  const { search } = useLocation();
  const viewed = useRef(false);
  const params = useMemo(() => readParams(search), [search]);
  const language = (i18n.resolvedLanguage || i18n.language || 'ko').startsWith('en') ? 'en' : 'ko';
  const destination = useMemo(() => {
    const query = new URLSearchParams({ source: SOURCE, experiment: EXPERIMENT });
    ALLOWED.forEach((key) => { if (Object.hasOwn(params, key)) query.set(key, params[key]); });
    return '/style-check?' + query.toString();
  }, [params]);

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackEvent('style_beta_landing_view', {
      experiment_id: EXPERIMENT,
      ...Object.fromEntries(TRACKED.map((key) => [key, params[key]])),
      language,
    });
  }, [language, params]);

  const handleCta = (location) => {
    try {
      sessionStorage.setItem('style_beta_experiment', EXPERIMENT);
      sessionStorage.setItem('style_beta_source', SOURCE);
      sessionStorage.setItem('style_beta_started_at', new Date().toISOString());
      TRACKED.forEach((key) => {
        if (params[key] !== undefined) sessionStorage.setItem('style_beta_' + key, params[key]);
      });
    } catch {
      // Navigation continues when storage is unavailable.
    }
    trackEvent('style_beta_cta_click', {
      experiment_id: EXPERIMENT,
      cta_location: location,
      ...Object.fromEntries(TRACKED.map((key) => [key, params[key]])),
      language,
    });
  };

  const heroBenefits = t('style_beta.hero.benefits', { returnObjects: true });
  const areas = t('style_beta.preview.areas', { returnObjects: true });
  const cards = t('style_beta.benefits.cards', { returnObjects: true });
  const steps = t('style_beta.process.steps', { returnObjects: true });
  const tips = t('style_beta.process.photo_tips', { returnObjects: true });

  return (
    <div className="style-beta-page">
      <Helmet>
        <html lang={language} />
        <title>{t('style_beta.meta.title')}</title>
        <meta name="description" content={t('style_beta.meta.description')} />
        <meta property="og:title" content={t('style_beta.meta.title')} />
        <meta property="og:description" content={t('style_beta.meta.description')} />
      </Helmet>
      <section className="style-beta-hero" aria-labelledby="style-beta-title">
        <div className="style-beta-hero-copy">
          <p className="style-beta-badge">{t('style_beta.hero.badge')}</p>
          <h1 id="style-beta-title"><span>{t('style_beta.hero.title_line_1')}</span><span>{t('style_beta.hero.title_line_2')}</span></h1>
          <p className="style-beta-lead">{t('style_beta.hero.description')}</p>
          <ul className="style-beta-checklist">{heroBenefits.map((item) => <li key={item}>{item}</li>)}</ul>
          <p className="style-beta-price">{t('style_beta.hero.price')}</p>
          <Link className="style-beta-cta" to={destination} onClick={() => handleCta('hero')}>{t('style_beta.hero.cta')}</Link>
          <p className="style-beta-note">{t('style_beta.hero.note')}</p>
        </div>
        <AnimatedPreview t={t} areas={areas} />
      </section>

      <RevealSection className="style-beta-section" aria-labelledby="benefit-title">
        <p className="style-beta-kicker">{t('style_beta.benefits.kicker')}</p><h2 id="benefit-title">{t('style_beta.benefits.title')}</h2>
        <div className="style-beta-cards">{cards.map((card, index) => <article key={card.title}><span aria-hidden="true">0{index + 1}</span><h3>{card.title}</h3><p>{card.description}</p></article>)}</div>
      </RevealSection>

      <RevealSection className="style-beta-section style-beta-process" aria-labelledby="process-title">
        <p className="style-beta-kicker">{t('style_beta.process.kicker')}</p><h2 id="process-title">{t('style_beta.process.title')}</h2>
        <ol>{steps.map((step, index) => <li key={step.title}><span aria-hidden="true">0{index + 1}</span><div><h3>{step.title}</h3><p>{step.description}</p></div></li>)}</ol>
        <aside><h3>{t('style_beta.process.tips_title')}</h3><ul>{tips.map((tip) => <li key={tip}>{tip}</li>)}</ul></aside>
      </RevealSection>

      <RevealSection className="style-beta-section style-beta-highlight" aria-labelledby="conditions-title">
        <p className="style-beta-kicker">{t('style_beta.conditions.kicker')}</p><h2 id="conditions-title">{t('style_beta.conditions.title')}</h2>
        <p>{t('style_beta.conditions.body')}</p><p className="style-beta-caution">{t('style_beta.conditions.caution')}</p>
      </RevealSection>

      <RevealSection className="style-beta-section style-beta-privacy" aria-labelledby="privacy-title">
        <p className="style-beta-kicker">{t('style_beta.privacy.kicker')}</p><h2 id="privacy-title">{t('style_beta.privacy.title')}</h2>
        <div className="style-beta-privacy-card"><p>{t('style_beta.privacy.body_1')}</p><p>{t('style_beta.privacy.body_2')}</p><p>{t('style_beta.privacy.body_3')}</p></div>
        <p className="style-beta-browser-note">{t('style_beta.privacy.browser_note')}</p><Link to="/privacy">{t('style_beta.privacy.link')}</Link>
      </RevealSection>

      <RevealSection className="style-beta-final" aria-labelledby="final-title">
        <p className="style-beta-badge">{t('style_beta.final_cta.badge')}</p>
        <h2 id="final-title"><span>{t('style_beta.final_cta.title_line_1')}</span><span>{t('style_beta.final_cta.title_line_2')}</span></h2>
        <Link className="style-beta-cta" to={destination} onClick={() => handleCta('bottom')}>{t('style_beta.final_cta.button')}</Link>
        <p>{t('style_beta.final_cta.note')}</p>
      </RevealSection>
    </div>
  );
}
